<template>
  <section
    v-if="productSets?.length"
    :aria-label="$t.shopTheLookHeading"
    :class="{
      'lg:m-a lg:px-20': productSets?.length === 1 && !isFullWidth,
          }"
    data-scroll-el="shopTheLook"
  >
    <div v-if="productSets.length === 1" class="relative grid gap-1 lg:cols-4 lg:rows-1">
      <base-button
        class="relative full lg:col-span-2"
        :class="{ 'order-last': primaryImagePosition === 'right' }"
        @click="$emit('openShopTheLook', productSets[0])"
      >
        <base-picture
          :alt="productSets[0].description"
          class="h-a w-full object-cover"
          :height="gallery.presets.src.height"
          :src="getImageTransformations(productSets[0].images[0], gallery.presets.src)"
          :width="gallery.presets.src.width"
        />
        <span v-style:c="labelColor" class="body-4 |body-3 absolute left-2 top-1 z-1 m-2 rounded-xs underscored">
          {{ labelText || productSets[0].text || $t.shopTheLookTileCta }}
        </span>
      </base-button>
      <div class="flex gap-1 overflow-x-auto lg:grid lg:col-span-2 lg:cols-2 lg:gap-1">
        <div
          v-for="{ id, thumbnail } in productSets[0].products?.slice(0, productSetImageThreshold)"
          :key="id"
          v-style:w="{
            sm: (productSets[0].products?.slice(0, productSetImageThreshold)?.length ?? 0) > 2 ? '42%' : '50%',
            lg: '100%',
          }"
          class="relative <lg:shrink-0"
        >
          <base-button  @click="PanelQuickshop.open({ productId: id })">
            <vf-icon class="absolute right-0 top-0 m-2" :name="quickshopIcon" size="md" />
            <base-picture
              :alt="productSets[0].description"
              class="h-a w-full object-cover"
              :height="gallery.presets.src.height"
              :props-img="{ class: 'w-a' }"
              :src="getImageTransformations(thumbnail, gallery.presets.src)"
              :width="gallery.presets.src.width"
            />
          </base-button>
        </div>
      </div>
    </div>
    <vf-carousel-elevate
      v-else
      class-controls="<lg:hidden h-10 w-10 bg-brand-1/50 p-2 c-white shadow-sm @hactive:bg-brand-1/90"
      :show-arrows="false"
      size-controls="md"
      :style="productSets.length === 2 && !isFullWidth && { maxWidth: '90rem', margin: 'auto' }"
    >
      <div
        v-for="productSet in productSets"
        :key="productSet.productSetId"
        v-style:w="{
          sm: '85%',
          lg: productSets.length >= 3 ? '40%' : `${100 / productSets.length}%`,
        }"
        class="relative"
      >
        <base-button class="relative full" @click="$emit('openShopTheLook', productSet)">
          <base-picture
            :alt="productSet.description"
            class="h-a w-full object-cover"
            :height="gallery.presets.src.height"
            :src="getImageTransformations(productSet.images[0], gallery.presets.src)"
            :width="gallery.presets.src.width"
          />
          <span v-style:c="labelColor" class="body-4 |body-3 absolute left-2 top-1 z-1 m-2 rounded-xs underscored">
            {{ labelText || formatProductSetTitle(productSet.text) || $t.shopTheLookTileCta }}
          </span>
        </base-button>
      </div>
    </vf-carousel-elevate>
    <panel-quickshop class-content="" />
  </section>
</template>

<script setup lang="ts">
import type { Dialogs } from '#build/dialogs'
import type { ProductSet } from '#root/api/clients/product/data-contracts'
import type { ResponsiveColor } from '#types/content'

defineProps<{
  productSets?: ProductSet[]
  primaryImagePosition?: 'left' | 'right'
  labelColor?: ResponsiveColor
  labelText?: string
  isFullWidth?: boolean
}>()
defineEmits<{ openShopTheLook: [ProductSet] }>()

const config = useAppConfig()
const {
  gallery,
  shopTheLook: {
    productSetImageThreshold = 4,
    quickshopIcon = 'quickshop'
  } = {}
} = config.pages?.pdp || {}
const { quickshopType } = config.components.product.shopTheLook

const PanelQuickshop = createPanel(quickshopType as keyof Dialogs)

const formatProductSetTitle = (str: string): string =>
  capitalize(str).replace(/(?:^|\s)\S/g, (c, i) => i === 0 ? c.toUpperCase() : c.toLowerCase())
</script>
